import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  Toggle,
  FormActions,
  CollapsibleSection,
  ReorderButtons,
  CTAGroup,
  SectionDivider,
  autoSlug,
} from "../../components/FormField";
import { MediaImageField } from "../../components/MediaPickerDialog";
import { showSuccess, showError, showSeoWarnings } from "../../lib/toast";
import { moveItem } from "../templates/shared";
import { generateUUID } from "../../commonjquery/CommonJquery";
import {
  server_post_data,
  get_product_page_data,
  product_page_save_update,
} from "../../serviceconnection/ServiceConnection";
import { SEOSection } from "../../components/SEOSection";
import { ICON_OPTIONS } from "../../lib/iconsData";
const CATEGORIES = [
  { value: "sap-business-one", label: "SAP Business One" },
  { value: "sap-cloud-erp", label: "SAP Cloud ERP" },
  { value: "crm-&-analytics", label: "CRM & Analytics" },
  { value: "integration-&-automation", label: "Integration & Automation" },
];

const ALL_SECTIONS_VISIBLE = {
  hero: true,
  whyProduct: true,
  delivers: true,
  challenges: true,
  implPartner: true,
  techStack: true,
  successStory: true,
  whoItsFor: true,
  video: true,
  faq: true,
  finalCta: true,
  seo: true,
};

const CATEGORY_TEMPLATES = {
  "sap-business-one": ALL_SECTIONS_VISIBLE,
  "sap-cloud-erp": ALL_SECTIONS_VISIBLE,
  "crm-&-analytics": ALL_SECTIONS_VISIBLE,
  "integration-&-automation": ALL_SECTIONS_VISIBLE,
};

function BulletRepeater({ bullets, setBullets, label = "Point" }) {
  return (
    <div className="space-y-2">
      {bullets.map((b, i) => (
        <div key={i} className="flex gap-2">
          <TextInput
            value={b}
            onChange={(e) =>
              setBullets(
                bullets.map((x, idx) => (idx === i ? e.target.value : x)),
              )
            }
            placeholder={`${label} ${i + 1}`}
            className="flex-1"
          />
          <ReorderButtons
            index={i}
            total={bullets.length}
            onMove={(f, t) => setBullets(moveItem(bullets, f, t))}
          />
          {bullets.length > 1 && (
            <button
              type="button"
              onClick={() => setBullets(bullets.filter((_, idx) => idx !== i))}
              className="text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => setBullets([...bullets, ""])}
        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
        style={{
          background: "hsl(var(--accent-muted))",
          color: "hsl(var(--accent))",
        }}
      >
        <Plus className="w-3.5 h-3.5" /> Add {label}
      </button>
    </div>
  );
}

function TagRepeater({ items, setItems, label }) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-center">
          <TextInput
            value={item.name}
            onChange={(e) =>
              setItems(
                items.map((x, idx) =>
                  idx === i ? { name: e.target.value } : x,
                ),
              )
            }
            placeholder={`${label} ${i + 1}`}
            className="flex-1"
          />
          <ReorderButtons
            index={i}
            total={items.length}
            onMove={(f, t) => setItems(moveItem(items, f, t))}
          />
          {items.length > 1 && (
            <button
              type="button"
              onClick={() => setItems(items.filter((_, idx) => idx !== i))}
              className="text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => setItems([...items, { name: "" }])}
        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
        style={{
          background: "hsl(var(--accent-muted))",
          color: "hsl(var(--accent))",
        }}
      >
        <Plus className="w-3.5 h-3.5" /> Add {label}
      </button>
    </div>
  );
}

function TechTagRepeater({ items, setItems, label }) {
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="p-3 rounded-lg border border-border space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground">
              {label} {i + 1}
            </p>
            <div className="flex items-center gap-2">
              <ReorderButtons
                index={i}
                total={items.length}
                onMove={(f, t) => setItems(moveItem(items, f, t))}
              />
              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                  className="text-destructive"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <Field label="Name">
            <TextInput
              value={item.name}
              onChange={(e) =>
                setItems(
                  items.map((x, idx) =>
                    idx === i ? { ...x, name: e.target.value } : x,
                  ),
                )
              }
              placeholder={`e.g. SAP HANA`}
            />
          </Field>
          <Field label="Logo / Image">
            <MediaImageField
              label="Select tech logo"
              hint="Recommended: 80×80px PNG/SVG"
              value={item.imageUrl}
              onChange={(v) =>
                setItems(
                  items.map((x, idx) =>
                    idx === i ? { ...x, imageUrl: v } : x,
                  ),
                )
              }
            />
          </Field>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setItems([...items, { name: "", imageUrl: "" }])}
        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
        style={{
          background: "hsl(var(--accent-muted))",
          color: "hsl(var(--accent))",
        }}
      >
        <Plus className="w-3.5 h-3.5" /> Add {label}
      </button>
    </div>
  );
}

function HeroStatsRepeater({ stats, setStats }) {
  return (
    <div className="space-y-2">
      {stats.map((s, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="w-32 shrink-0">
            <SelectInput
              value={s.icon}
              onChange={(e) =>
                setStats(
                  stats.map((x, idx) =>
                    idx === i ? { ...x, icon: e.target.value } : x,
                  ),
                )
              }
              options={ICON_OPTIONS}
            />
          </div>
          <TextInput
            value={s.title}
            onChange={(e) =>
              setStats(
                stats.map((x, idx) =>
                  idx === i ? { ...x, title: e.target.value } : x,
                ),
              )
            }
            placeholder="e.g. 150+ Clients"
            className="flex-1"
          />
          <TextInput
            value={s.decp}
            onChange={(e) =>
              setStats(
                stats.map((x, idx) =>
                  idx === i ? { ...x, decp: e.target.value } : x,
                ),
              )
            }
            placeholder="e.g. Across Australia"
            className="flex-1"
          />
          <ReorderButtons
            index={i}
            total={stats.length}
            onMove={(f, t) => setStats(moveItem(stats, f, t))}
          />
          {stats.length > 1 && (
            <button
              type="button"
              onClick={() => setStats(stats.filter((_, idx) => idx !== i))}
              className="text-destructive pt-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          setStats([...stats, { icon: "Shield", title: "", decp: "" }])
        }
        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
        style={{
          background: "hsl(var(--accent-muted))",
          color: "hsl(var(--accent))",
        }}
      >
        <Plus className="w-3.5 h-3.5" /> Add Stat
      </button>
    </div>
  );
}

function StatsRepeater({ stats, setStats }) {
  return (
    <div className="space-y-2">
      {stats.map((s, i) => (
        <div key={i} className="flex gap-2">
          <TextInput
            value={s.value}
            onChange={(e) =>
              setStats(
                stats.map((x, idx) =>
                  idx === i ? { ...x, value: e.target.value } : x,
                ),
              )
            }
            placeholder="e.g. 40%"
            className="w-28"
          />
          <TextInput
            value={s.label}
            onChange={(e) =>
              setStats(
                stats.map((x, idx) =>
                  idx === i ? { ...x, label: e.target.value } : x,
                ),
              )
            }
            placeholder="e.g. Faster reporting"
            className="flex-1"
          />
          <ReorderButtons
            index={i}
            total={stats.length}
            onMove={(f, t) => setStats(moveItem(stats, f, t))}
          />
          {stats.length > 1 && (
            <button
              type="button"
              onClick={() => setStats(stats.filter((_, idx) => idx !== i))}
              className="text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => setStats([...stats, { value: "", label: "" }])}
        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
        style={{
          background: "hsl(var(--accent-muted))",
          color: "hsl(var(--accent))",
        }}
      >
        <Plus className="w-3.5 h-3.5" /> Add Stat
      </button>
    </div>
  );
}

export function ProductForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uuid, setUuid] = useState("");

  // ─── Basic ────────────────────────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("sap-business-one");

  const template =
    CATEGORY_TEMPLATES[category] ?? CATEGORY_TEMPLATES["sap-business-one"];

  // ─── SEO ──────────────────────────────────────────────────────────────────
  const [seoData, setSeoData] = useState({});
  const handleSeoChange = (field, value) =>
    setSeoData((prev) => ({ ...prev, [field]: value }));

  // ─── Section toggles ──────────────────────────────────────────────────────
  const [sections, setSections] = useState({
    hero: true,
    whyProduct: true,
    delivers: true,
    challenges: true,
    implPartner: true,
    techStack: true,
    whoItsFor: true,
    video: true,
    finalCta: true,
    seo: true,
  });
  const toggle = (k) => setSections((s) => ({ ...s, [k]: !s[k] }));

  // ─── HERO ─────────────────────────────────────────────────────────────────
  const [hero, setHero] = useState({
    heading: "",
    highlight: "",
    subheading: "",
    bgImageUrl: "",
    bgImageAlt: "",
    bgVideoUrl: "",
    overlayOpacity: 50,
  });
  const [heroBullets, setHeroBullets] = useState([""]);
  const [heroPrimary, setHeroPrimary] = useState({
    text: "",
    url: "",
    newTab: false,
  });
  const [heroSecondary, setHeroSecondary] = useState({
    text: "",
    url: "",
    newTab: false,
  });
  const [heroStats, setHeroStats] = useState([
    { icon: "Shield", title: "", subtitle: "" },
  ]);

  // ─── WHY [PRODUCT] ────────────────────────────────────────────────────────
  const [whyProduct, setWhyProduct] = useState({
    heading: "",
    description: "",
    imageUrl: "",
    imageAlt: "",
    imagePosition: "right",
    cta_btn1_text: "",
    cta_btn1_link: "",
  });
  const [whyBullets, setWhyBullets] = useState([""]);

  // ─── Key Capabilities ─────────────────────────────────────────────────────
  const [delivers, setDelivers] = useState({ heading: "", description: "" });
  const [deliverCards, setDeliverCards] = useState([
    { icon: "Zap", title: "", description: "" },
  ]);

  // ─── CHALLENGES & SOLUTIONS ───────────────────────────────────────────────
  const [chalActive, setChalActive] = useState(true);
  const [chalHeading, setChalHeading] = useState("");
  const [chalList, setChalList] = useState([""]);
  const [solveList, setSolveList] = useState([""]);
  const [chalCta, setChalCta] = useState({ text: "", url: "", newTab: false });

  // ─── IMPLEMENTATION PARTNER ───────────────────────────────────────────────
  const [implActive, setImplActive] = useState(true);
  const [implHeading, setImplHeading] = useState("");
  const [implDesc, setImplDesc] = useState("");
  const [implSteps, setImplSteps] = useState([{ title: "", description: "" }]);
  const [implCta, setImplCta] = useState({ text: "", url: "", newTab: false });

  // ─── TECHNOLOGY STACK ─────────────────────────────────────────────────────
  const [techStack, setTechStack] = useState({ heading: "", subheading: "" });
  const [techStackTags, setTechStackTags] = useState([
    { name: "", imageUrl: "" },
  ]);
  const [techStackCta, setTechStackCta] = useState({
    text: "",
    url: "",
    newTab: false,
  });

  // ─── WHO IT'S FOR ─────────────────────────────────────────────────────────
  const [whoItsFor, setWhoItsFor] = useState({ heading: "" });
  const [industries, setIndustries] = useState([{ name: "" }]);
  const [businessSizes, setBusinessSizes] = useState([{ name: "" }]);
  const [keyRoles, setKeyRoles] = useState([{ name: "" }]);
  const [whoItsForImage, setWhoItsForImg] = useState({
    whoItsForImage: "",
  });
  const [whoItsForImageAlt, setWhoItsForImgAlt] = useState({
    whoItsForImageAlt: "",
  });

  // ─── VIDEO ────────────────────────────────────────────────────────────────
  const [videoState, setVideoState] = useState({
    heading: "",
    url: "",
    thumbnailUrl: "",
    thumbnailAlt: "",
    autoplay: false,
    controls: true,
  });

  // ─── FINAL CTA ────────────────────────────────────────────────────────────
  const [finalCta, setFinalCta] = useState({
    heading: "",
    description: "",
    bgImageUrl: "",
    bgImageAlt: "",
    bgVideoUrl: "",
    overlayOpacity: 50,
  });
  const [ctaPrimary, setCtaPrimary] = useState({
    text: "",
    url: "",
    newTab: false,
  });
  // ─── FAQ (product-specific — never the generic SAP B1 FAQ) ───────────────
  const [faqHeading, setFaqHeading] = useState("");
  const [faqs, setFaqs] = useState([{ q: "", a: "" }]);

  const [ctaSecondary, setCtaSecondary] = useState({
    text: "",
    url: "",
    newTab: false,
  });

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const handleTitleChange = (v) => {
    setTitle(v);
  };

  // ─── FETCH existing data on edit ──────────────────────────────────────────
  useEffect(() => {
    if (mode === "edit" && id) {
      const fetchExisting = async () => {
        try {
          const formData = new FormData();
          formData.append("main_id", id);
          const response = await server_post_data(
            get_product_page_data,
            formData,
          );
          const d = response.data?.data ?? null;
          if (!d) return;

          // ── Basic ──────────────────────────────────────────────────────
          setTitle(d.product_name ?? "");
          setCategory(d.product_category ?? "sap-business-one");
          setUuid(d.uuid ?? generateUUID());

          // ── Section toggles ────────────────────────────────────────────
          setSections({
            hero: true,
            whyProduct: d.show_prod_over ?? true,
            delivers: d.show_key_capabilities ?? true,
            challenges: d.show_challenges ?? true,
            implPartner: d.show_deliver_section ?? true,
            techStack: d.show_tech_stack ?? true,
            whoItsFor: d.show_who_its_for ?? true,
            video: d.show_video_section ?? true,
            finalCta: d.show_footer_cta ?? true,
            seo: true,
          });

          // ── Hero ──────────────────────────────────────────────────────
          setHero({
            heading: d.banner_heading ?? "",
            highlight: d.banner_highlight_word ?? "",
            subheading: d.banner_subheading ?? "",
            bgImageUrl: d.banner_bg_image ?? "",
            bgImageAlt: d.banner_bg_image_alt ?? "",
            bgVideoUrl: d.banner_bg_video_url ?? "",
            overlayOpacity: d.banner_overlay_opacity ?? 50,
          });
          setHeroBullets(d.banner_points?.length ? d.banner_points : [""]);
          setHeroPrimary({
            text: d.banner_btn1_text ?? "",
            url: d.banner_btn1_link ?? "",
            newTab: d.banner_btn1_new_tab ?? false,
          });
          setHeroSecondary({
            text: d.banner_btn2_text ?? "",
            url: d.banner_btn2_link ?? "",
            newTab: d.banner_btn2_new_tab ?? false,
          });
          setHeroStats(
            d.banner_badges?.length
              ? d.banner_badges
              : [{ icon: "Shield", title: "", decp: "" }],
          );

          // ── Why Product ───────────────────────────────────────────────
          setWhyProduct({
            heading: d.prod_over_heading ?? "",
            description: d.prod_over_description ?? "",
            imageUrl: d.prod_over_image_url ?? "",
            imageAlt: d.prod_over_image_alt ?? "",
            imagePosition: d.why_image_position ?? "right",
            cta_btn1_text: d.prod_over_cta_btn_text ?? "/",
            cta_btn1_link: d.prod_over_cta_btn_link ?? "/",
          });
          setWhyBullets(d.prod_over_bullets?.length ? d.prod_over_bullets : []);

          // ── Delivers ──────────────────────────────────────────────────
          setDelivers({
            heading: d.key_capabilities_heading ?? "",
            description: d.key_capabilities_description ?? "",
          });
          setDeliverCards(
            d.key_capabilities_cards?.length
              ? d.key_capabilities_cards
              : [{ icon: "Zap", title: "", description: "" }],
          );

          // ── Challenges ────────────────────────────────────────────────
          setChalActive(d.show_challenges ?? true);
          setChalHeading(d.challenge_heading ?? "");
          setChalList(d.common_challenges?.length ? d.common_challenges : [""]);
          setSolveList(d.our_solutions?.length ? d.our_solutions : [""]);
          setChalCta({
            text: d.challenge_btn_text ?? "",
            url: d.challenge_btn_link ?? "",
          });

          // ── Implementation Partner ────────────────────────────────────
          setImplActive(d.show_deliver_section ?? true);
          setImplHeading(d.deliver_heading ?? "");
          setImplDesc(d.deliver_description ?? "");
          setImplSteps(
            d.deliver_points?.length
              ? d.deliver_points.map((s) => ({
                  title: s.title,
                  description: s.description,
                }))
              : [{ title: "", description: "" }],
          );
          setImplCta({
            text: d.deliver_cta_text ?? "",
            url: d.deliver_cta_url ?? "",
          });

          // ── Tech Stack ────────────────────────────────────────────────
          setTechStack({
            heading: d.tech_stack_heading ?? "",
            subheading: d.tech_stack_description ?? "",
          });
          setTechStackTags(
            d.tech_stack_list?.length
              ? d.tech_stack_list.map((t) =>
                  typeof t === "string"
                    ? { name: t, imageUrl: "" }
                    : {
                        name: t.name ?? t.title ?? "",
                        imageUrl: t.image ?? t.imageUrl ?? "",
                      },
                )
              : [{ name: "", imageUrl: "" }],
          );
          setTechStackCta({
            text: d.tech_stack_btn_text ?? "",
            url: d.tech_stack_btn_link ?? "",
            newTab: d.tech_stack_btn_new_tab ?? false,
          });

          // ── Who It's For ──────────────────────────────────────────────
          setWhoItsFor({ heading: d.who_its_for_heading ?? "" });
          setWhoItsForImg({ whoItsForImage: d.who_its_for_image ?? "" });
          setIndustries(
            d.who_industries?.length
              ? d.who_industries.map((n) => ({ name: n }))
              : [{ name: "" }],
          );
          setBusinessSizes(
            d.who_business_sizes?.length
              ? d.who_business_sizes.map((n) => ({ name: n }))
              : [{ name: "" }],
          );
          setKeyRoles(
            d.who_key_roles?.length
              ? d.who_key_roles.map((n) => ({ name: n }))
              : [{ name: "" }],
          );

          // ── Video ─────────────────────────────────────────────────────
          setVideoState({
            heading: d.video_heading ?? "",
            url: d.video_url ?? "",
            thumbnailUrl: d.video_thumbnail ?? "",
            thumbnailAlt: d.video_thumbnail_alt ?? "",
            autoplay: d.video_autoplay ?? false,
            controls: d.video_controls ?? true,
          });

          // ── Final CTA ─────────────────────────────────────────────────
          setFinalCta({
            heading: d.cta_heading ?? "",
            description: d.cta_description ?? "",
            bgImageUrl: d.final_cta_bg_image_url ?? "",
            bgImageAlt: d.final_cta_bg_image_alt ?? "",
            bgVideoUrl: d.final_cta_bg_video_url ?? "",
            overlayOpacity: d.final_cta_overlay_opacity ?? 50,
          });
          setCtaPrimary({
            text: d.cta_btn1_text ?? "",
            url: d.cta_btn1_link ?? "",
            newTab: d.cta_btn1_new_tab ?? false,
          });
          setCtaSecondary({
            text: d.cta_btn2_text ?? "",
            url: d.cta_btn2_link ?? "",
            newTab: d.cta_btn2_new_tab ?? false,
          });

          // ── FAQ ───────────────────────────────────────────────────────
          setFaqHeading(d.faq_heading ?? "");
          let loadedFaqs = d.faqs;
          if (typeof loadedFaqs === "string") {
            try {
              loadedFaqs = JSON.parse(loadedFaqs);
            } catch {
              loadedFaqs = [];
            }
          }
          setFaqs(
            Array.isArray(loadedFaqs) && loadedFaqs.length
              ? loadedFaqs.map((f) => ({
                  q: f.q ?? f.question ?? "",
                  a: f.a ?? f.answer ?? "",
                }))
              : [{ q: "", a: "" }],
          );

          // ── SEO ───────────────────────────────────────────────────────
          const sd = d.seo_data ?? {};
          setSeoData({
            seo_id: sd.seo_id ?? 0,
            title_name: sd.title_name ?? "",
            description: sd.description ?? "",
            image: sd.image ?? "",
            actual_url: sd.actual_url ?? "",
            link_url: sd.link_url ?? "",
            favicon: sd.favicon ?? "",
            keywords: sd.keywords ?? "",
            changefreq: sd.changefreq ?? "",
            priority_screen: sd.priority ?? "0.8",
          });
        } catch (err) {
          console.error("Failed to load product:", err);
          showError("Failed to load product data. Please try again.");
        }
      };
      fetchExisting();
    }
  }, [mode, id]);

  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required";
    return e;
  };

  // ─── BUILD PAYLOAD ────────────────────────────────────────────────────────
  const buildPayload = (saveStatus) => {
    const localId = mode === "edit" ? Number(id) : Date.now();
    const uuId = mode === "edit" ? uuid || generateUUID() : generateUUID();

    return {
      p_id: mode === "edit" ? localId : 0,
      uuid: uuId,
      product_name: title,
      product_category: category,

      banner_heading: hero.heading,
      banner_highlight_word: hero.highlight,
      banner_subheading: hero.subheading,
      banner_bg_image: hero.bgImageUrl,
      banner_bg_image_alt: hero.bgImageAlt,
      banner_overlay_opacity: hero.overlayOpacity,
      banner_points: heroBullets.filter(Boolean),
      banner_btn1_text: heroPrimary.text,
      banner_btn1_link: heroPrimary.url,
      banner_btn2_text: heroSecondary.text,
      banner_btn2_link: heroSecondary.url,
      banner_badges: heroStats.filter((s) => s.title || s.decp),

      show_prod_over: sections.whyProduct,
      prod_over_heading: whyProduct.heading,
      prod_over_description: whyProduct.description,
      prod_over_bullets: whyBullets.filter(Boolean),
      prod_over_image_url: whyProduct.imageUrl,
      prod_over_image_alt: whyProduct.imageAlt,
      prod_over_cta_btn_text: whyProduct.cta_btn1_text,
      prod_over_cta_btn_link: whyProduct.cta_btn1_link,

      show_key_capabilities: sections.delivers,
      key_capabilities_heading: delivers.heading,
      key_capabilities_description: delivers.description,
      key_capabilities_cards: deliverCards.map((c) => ({
        icon: c.icon,
        title: c.title,
        description: c.description,
      })),

      show_challenges: chalActive,
      challenge_heading: chalHeading,
      common_challenges: chalList.filter(Boolean),
      our_solutions: solveList.filter(Boolean),
      challenge_btn_text: chalCta.text,
      challenge_btn_link: chalCta.url,

      show_deliver_section: implActive,
      deliver_heading: implHeading,
      deliver_description: implDesc,
      deliver_points: implSteps.map((s, i) => ({
        step_number: i + 1,
        title: s.title,
        description: s.description,
      })),
      deliver_cta_text: implCta.text,
      deliver_cta_url: implCta.url,

      show_tech_stack: sections.techStack,
      tech_stack_heading: techStack.heading,
      tech_stack_description: techStack.subheading,
      tech_stack_list: techStackTags
        .filter((t) => t.name)
        .map((t) => ({ name: t.name, image: t.imageUrl })),
      tech_stack_btn_text: techStackCta.text,
      tech_stack_btn_link: techStackCta.url,

      show_who_its_for: sections.whoItsFor,
      who_its_for_heading: whoItsFor.heading,
      who_industries: industries.map((i) => i.name).filter(Boolean),
      who_business_sizes: businessSizes.map((i) => i.name).filter(Boolean),
      who_key_roles: keyRoles.map((i) => i.name).filter(Boolean),
      who_its_for_image: whoItsForImage.whoItsForImage,
      who_its_for_image_alt: whoItsForImageAlt.whoItsForImageAlt,

      show_video_section: sections.video,
      video_heading: videoState.heading,
      video_url: videoState.url,
      video_thumbnail: videoState.thumbnailUrl,
      video_thumbnail_alt: videoState.thumbnailAlt,

      show_footer_cta: sections.finalCta,
      cta_heading: finalCta.heading,
      cta_description: finalCta.description,
      cta_btn1_text: ctaPrimary.text,
      cta_btn1_link: ctaPrimary.url,
      cta_btn2_text: ctaSecondary.text,
      cta_btn2_link: ctaSecondary.url,

      faq_heading: faqHeading,
      faqs: faqs
        .map((f) => ({ q: f.q.trim(), a: f.a.trim() }))
        .filter((f) => f.q && f.a),

      title_name: seoData.title_name ?? "",
      description: seoData.description ?? "",
      image: seoData.image ?? "",
      favicon: seoData.favicon ?? "",
      actual_url: seoData.actual_url ?? "",
      link_url: seoData.link_url ?? "",
      keywords: seoData.keywords ?? "",
      changefreq: seoData.changefreq ?? "",
      priority: seoData.priority_screen ?? "0.8",

      // Unwanted Data
      show_client_success: true,
      client_success_heading: "Trusted by Leaders",
      client_success_description: "See what our happy clients have to say.",
      client_success: [
        { name: "John Doe", role: "CEO", comment: "Amazing experience!" },
      ],

      show_insights: true,
      insights_heading: "Latest Blog & News",
      insights_cards: [
        { title: "ERP Trends 2026", link: "/blog/1", image: "img1.jpg" },
      ],
      integration_heading: "sdsdasd",
    };
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async (saveStatus) => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      showError("Please fix the errors");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const payload = buildPayload(saveStatus);

      // ── Console log payload for debugging ─────────────────────────────
      console.log("=== ProductForm Payload ===", payload);

      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (typeof value === "boolean") {
          formData.append(key, value ? 1 : 0);
        } 
        else if (typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } 
        else {
          formData.append(key, value);
        }
      });

      const saveRes = await server_post_data(product_page_save_update, payload);

      showSuccess(mode === "create" ? "Product created!" : "Product updated!");
      showSeoWarnings(saveRes);
      navigate("/products");
    } catch {
      showError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/products")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              {mode === "create" ? "Add Product" : "Edit Product"}
            </h1>
            <p className="page-subtitle">Product page editor</p>
          </div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="section-panel">
        <div className="section-panel-header">
          <h2 className="section-panel-title">Basic Info</h2>
        </div>
        <div className="section-panel-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Product Name" required error={errors.title}>
              <TextInput
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="SAP Business One"
                error={!!errors.title}
              />
            </Field>
            <Field label="Category">
              <SelectInput
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                options={CATEGORIES}
              />
            </Field>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground">
              Active template:
            </span>
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              {CATEGORIES.find((c) => c.value === category)?.label}
            </span>
          </div>
        </div>
      </div>

      {/* ===================== HERO ===================== */}
      {template.hero && (
        <CollapsibleSection
          title="Hero Section"
          active={sections.hero}
          onToggleActive={() => toggle("hero")}
        >
          <Field label="Main Heading">
            <TextInput
              value={hero.heading}
              onChange={(e) =>
                setHero((h) => ({ ...h, heading: e.target.value }))
              }
              placeholder="The #1 ERP for SMEs"
            />
          </Field>
          <Field label="Highlight Word">
            <TextInput
              value={hero.highlight}
              onChange={(e) =>
                setHero((h) => ({ ...h, highlight: e.target.value }))
              }
              placeholder="#1"
            />
          </Field>
          <Field label="Subheading">
            <TextArea
              value={hero.subheading}
              onChange={(e) =>
                setHero((h) => ({ ...h, subheading: e.target.value }))
              }
              placeholder="Supporting description"
            />
          </Field>
          <div className="pt-3 border-t border-border space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Background
            </p>
            <Field label="Background Image">
              <MediaImageField
                label="Select background image"
                hint="Recommended: 1920×1080px"
                value={hero.bgImageUrl}
                onChange={(v) => setHero((h) => ({ ...h, bgImageUrl: v }))}
                alt={hero.bgImageAlt}
                onAltChange={(v) => setHero((h) => ({ ...h, bgImageAlt: v }))}
              />
            </Field>
          </div>
          <div className="pt-3 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              "What We Deliver"
            </p>
            <BulletRepeater
              bullets={heroBullets}
              setBullets={setHeroBullets}
              label="Bullet"
            />
          </div>
          <SectionDivider title="Buttons" />
          <CTAGroup
            label="Primary Button"
            text={heroPrimary.text}
            url={heroPrimary.url}
            newTab={heroPrimary.newTab}
            onTextChange={(v) => setHeroPrimary((b) => ({ ...b, text: v }))}
            onUrlChange={(v) => setHeroPrimary((b) => ({ ...b, url: v }))}
            onNewTabChange={(v) => setHeroPrimary((b) => ({ ...b, newTab: v }))}
          />
          <CTAGroup
            label="Secondary Button"
            text={heroSecondary.text}
            url={heroSecondary.url}
            newTab={heroSecondary.newTab}
            onTextChange={(v) => setHeroSecondary((b) => ({ ...b, text: v }))}
            onUrlChange={(v) => setHeroSecondary((b) => ({ ...b, url: v }))}
            onNewTabChange={(v) =>
              setHeroSecondary((b) => ({ ...b, newTab: v }))
            }
          />
          <div className="pt-3 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Stats Row
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              Icon · Heading (e.g. "150+ Clients") · Sub-label (e.g. "Across
              Australia")
            </p>
            <HeroStatsRepeater stats={heroStats} setStats={setHeroStats} />
          </div>
        </CollapsibleSection>
      )}

      {/* ===================== WHY [PRODUCT] ===================== */}
      {template.whyProduct && (
        <CollapsibleSection
          title="Product Overview"
          active={sections.whyProduct}
          onToggleActive={() => toggle("whyProduct")}
        >
          <Field label="Heading">
            <TextInput
              value={whyProduct.heading}
              onChange={(e) =>
                setWhyProduct((s) => ({ ...s, heading: e.target.value }))
              }
              placeholder="e.g. Why Salesforce with ThynkProcess?"
            />
          </Field>
          <Field label="Description">
            <TextArea
              value={whyProduct.description}
              onChange={(e) =>
                setWhyProduct((s) => ({ ...s, description: e.target.value }))
              }
              placeholder="Description text"
            />
          </Field>
          <div className="pt-3 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Bullet Points
            </p>
            <BulletRepeater
              bullets={whyBullets}
              setBullets={setWhyBullets}
              label="Point"
            />
          </div>
          <Field label="Section Image">
            <MediaImageField
              label="Upload section image"
              hint="800×500px"
              value={whyProduct.imageUrl}
              onChange={(v) => setWhyProduct((s) => ({ ...s, imageUrl: v }))}
              alt={whyProduct.imageAlt}
              onAltChange={(v) => setWhyProduct((s) => ({ ...s, imageAlt: v }))}
            />
          </Field>
          <CTAGroup
            label="CTA Button"
            text={whyProduct.cta_btn1_text}
            url={whyProduct.cta_btn1_link}
            onTextChange={(v) =>
              setWhyProduct((b) => ({ ...b, cta_btn1_text: v }))
            }
            onUrlChange={(v) =>
              setWhyProduct((b) => ({ ...b, cta_btn1_link: v }))
            }
          />
        </CollapsibleSection>
      )}

      {/* ===================== Key Capabilities ===================== */}
      {template.delivers && (
        <CollapsibleSection
          title="Key Capabilities"
          active={sections.delivers}
          onToggleActive={() => toggle("delivers")}
        >
          <Field label="Heading">
            <TextInput
              value={delivers.heading}
              onChange={(e) =>
                setDelivers((s) => ({ ...s, heading: e.target.value }))
              }
              placeholder="What ThynkProcess Delivers on [Product]"
            />
          </Field>
          <Field label="Description">
            <TextArea
              value={delivers.description}
              onChange={(e) =>
                setDelivers((s) => ({ ...s, description: e.target.value }))
              }
              placeholder="e.g. ThynkProcess tailors every implementation..."
            />
          </Field>
          <SectionDivider title="Capability Cards" />
          {deliverCards.map((c, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Card {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={deliverCards.length}
                    onMove={(f, t) =>
                      setDeliverCards(moveItem(deliverCards, f, t))
                    }
                  />
                  {deliverCards.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setDeliverCards((cs) =>
                          cs.filter((_, idx) => idx !== i),
                        )
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <Field label="Icon">
                <SelectInput
                  value={c.icon}
                  onChange={(e) =>
                    setDeliverCards((cs) =>
                      cs.map((x, idx) =>
                        idx === i ? { ...x, icon: e.target.value } : x,
                      ),
                    )
                  }
                  options={ICON_OPTIONS}
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Title">
                  <TextInput
                    value={c.title}
                    onChange={(e) =>
                      setDeliverCards((cs) =>
                        cs.map((x, idx) =>
                          idx === i ? { ...x, title: e.target.value } : x,
                        ),
                      )
                    }
                    placeholder="Feature title"
                  />
                </Field>
                <Field label="Description">
                  <TextInput
                    value={c.description}
                    onChange={(e) =>
                      setDeliverCards((cs) =>
                        cs.map((x, idx) =>
                          idx === i ? { ...x, description: e.target.value } : x,
                        ),
                      )
                    }
                    placeholder="Feature description"
                  />
                </Field>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setDeliverCards((c) => [
                ...c,
                { icon: "Zap", title: "", description: "" },
              ])
            }
            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
            style={{
              background: "hsl(var(--accent-muted))",
              color: "hsl(var(--accent))",
            }}
          >
            <Plus className="w-3.5 h-3.5" /> Add Card
          </button>
        </CollapsibleSection>
      )}

      {/* ===================== CHALLENGES & SOLUTIONS ===================== */}
      {template.challenges && (
        <CollapsibleSection
          title="Challenges & Solutions Section"
          active={chalActive}
          onToggleActive={setChalActive}
        >
          <Field label="Heading">
            <TextInput
              value={chalHeading}
              onChange={(e) => setChalHeading(e.target.value)}
              placeholder="e.g. Common Challenges Salesforce CRM Solves"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-border">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Challenge Column
              </p>
              <BulletRepeater
                bullets={chalList}
                setBullets={setChalList}
                label="Challenge"
              />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                "How We Solve It" Column
              </p>
              <BulletRepeater
                bullets={solveList}
                setBullets={setSolveList}
                label="Solution"
              />
            </div>
          </div>
          <CTAGroup
            label="Learn More Button"
            text={chalCta.text}
            url={chalCta.url}
            newTab={chalCta.newTab}
            onTextChange={(v) => setChalCta((b) => ({ ...b, text: v }))}
            onUrlChange={(v) => setChalCta((b) => ({ ...b, url: v }))}
            onNewTabChange={(v) => setChalCta((b) => ({ ...b, newTab: v }))}
          />
        </CollapsibleSection>
      )}

      {/* ===================== IMPLEMENTATION PARTNER ===================== */}
      {template.implPartner && (
        <CollapsibleSection
          title="Our Delivery"
          active={implActive}
          onToggleActive={setImplActive}
        >
          <Field label="Heading">
            <TextInput
              value={implHeading}
              onChange={(e) => setImplHeading(e.target.value)}
              placeholder="e.g. ThynkProcess — Your Implementation Partner"
            />
          </Field>
          <Field label="Description">
            <TextArea
              value={implDesc}
              onChange={(e) => setImplDesc(e.target.value)}
              placeholder="Supporting paragraph text"
            />
          </Field>
          <SectionDivider title="Delivery Steps" />
          {implSteps.map((s, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Step {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={implSteps.length}
                    onMove={(f, t) => setImplSteps(moveItem(implSteps, f, t))}
                  />
                  {implSteps.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setImplSteps((ss) => ss.filter((_, idx) => idx !== i))
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <Field label="Step Title">
                <TextInput
                  value={s.title}
                  onChange={(e) =>
                    setImplSteps((ss) =>
                      ss.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="e.g. Discovery & Planning"
                />
              </Field>
              <Field label="Step Description">
                <TextArea
                  value={s.description}
                  onChange={(e) =>
                    setImplSteps((ss) =>
                      ss.map((x, idx) =>
                        idx === i ? { ...x, description: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Brief description of this step"
                />
              </Field>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setImplSteps((ss) => [...ss, { title: "", description: "" }])
            }
            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
            style={{
              background: "hsl(var(--accent-muted))",
              color: "hsl(var(--accent))",
            }}
          >
            <Plus className="w-3.5 h-3.5" /> Add Step
          </button>
          <CTAGroup
            label="CTA Button"
            text={implCta.text}
            url={implCta.url}
            newTab={implCta.newTab}
            onTextChange={(v) => setImplCta((b) => ({ ...b, text: v }))}
            onUrlChange={(v) => setImplCta((b) => ({ ...b, url: v }))}
            onNewTabChange={(v) => setImplCta((b) => ({ ...b, newTab: v }))}
          />
        </CollapsibleSection>
      )}

      {/* ===================== TECHNOLOGY STACK ===================== */}
      {template.techStack && (
        <CollapsibleSection
          title="Technology Stack Section"
          active={sections.techStack}
          onToggleActive={() => toggle("techStack")}
        >
          <Field label="Heading">
            <TextInput
              value={techStack.heading}
              onChange={(e) =>
                setTechStack((s) => ({ ...s, heading: e.target.value }))
              }
              placeholder="e.g. Salesforce in Your Connected Technology Stack"
            />
          </Field>
          <Field label="Subheading">
            <TextArea
              value={techStack.subheading}
              onChange={(e) =>
                setTechStack((s) => ({ ...s, subheading: e.target.value }))
              }
              placeholder="Supporting description"
            />
          </Field>
          <SectionDivider title="Product Name Pills" />
          <TechTagRepeater
            items={techStackTags}
            setItems={setTechStackTags}
            label="Product"
          />
          <CTAGroup
            label="Explore All Products Button"
            text={techStackCta.text}
            url={techStackCta.url}
            newTab={techStackCta.newTab}
            onTextChange={(v) => setTechStackCta((b) => ({ ...b, text: v }))}
            onUrlChange={(v) => setTechStackCta((b) => ({ ...b, url: v }))}
            onNewTabChange={(v) =>
              setTechStackCta((b) => ({ ...b, newTab: v }))
            }
          />
        </CollapsibleSection>
      )}

      {/* ===================== IS THIS RIGHT FOR YOU? ===================== */}
      {template.whoItsFor && (
        <CollapsibleSection
          title="Is This Right For You?"
          active={sections.whoItsFor}
          onToggleActive={() => toggle("whoItsFor")}
        >
          <Field label="Heading">
            <TextInput
              value={whoItsFor.heading}
              onChange={(e) =>
                setWhoItsFor((s) => ({ ...s, heading: e.target.value }))
              }
              placeholder="Is This Right For You?"
            />
          </Field>
          <Field label="Section Image">
            <MediaImageField
              label="Upload section image"
              hint="800×500px"
              value={whoItsForImage.whoItsForImage}
              onChange={(v) =>
                setWhoItsForImg((s) => ({ ...s, whoItsForImage: v }))
              }
              alt={whoItsForImageAlt.whoItsForImageAlt}
              onAltChange={(v) =>
                setWhoItsForImgAlt((s) => ({ ...s, whoItsForImageAlt: v }))
              }
            />
          </Field>
          <SectionDivider title="Industries" />
          <TagRepeater
            items={industries}
            setItems={setIndustries}
            label="Industry"
          />
          <SectionDivider title="Business Sizes" />
          <TagRepeater
            items={businessSizes}
            setItems={setBusinessSizes}
            label="Size"
          />
          <SectionDivider title="Key Roles" />
          <TagRepeater items={keyRoles} setItems={setKeyRoles} label="Role" />
        </CollapsibleSection>
      )}

      {/* ===================== VIDEO ===================== */}
      {template.video && (
        <CollapsibleSection
          title="Video Section"
          active={sections.video}
          onToggleActive={() => toggle("video")}
        >
          <Field label="Heading">
            <TextInput
              value={videoState.heading}
              onChange={(e) =>
                setVideoState((s) => ({ ...s, heading: e.target.value }))
              }
              placeholder="See It In Action"
            />
          </Field>
          <Field
            label="Video URL"
            hint="Select from media library or paste a YouTube / Vimeo embed URL"
          >
            <MediaImageField
              label="Select or paste video URL"
              hint="YouTube or Vimeo embed URL"
              value={videoState.url}
              onChange={(v) => setVideoState((s) => ({ ...s, url: v }))}
            />
          </Field>
          <Field label="Video Thumbnail">
            <MediaImageField
              label="Select video thumbnail"
              hint="1280×720px"
              value={videoState.thumbnailUrl}
              onChange={(v) =>
                setVideoState((s) => ({ ...s, thumbnailUrl: v }))
              }
              alt={videoState.thumbnailAlt}
              onAltChange={(v) =>
                setVideoState((s) => ({ ...s, thumbnailAlt: v }))
              }
            />
          </Field>
        </CollapsibleSection>
      )}

      {/* ===================== FINAL CTA ===================== */}
      {template.finalCta && (
        <CollapsibleSection
          title="Final CTA Section"
          active={sections.finalCta}
          onToggleActive={() => toggle("finalCta")}
        >
          <Field label="Heading">
            <TextInput
              value={finalCta.heading}
              onChange={(e) =>
                setFinalCta((c) => ({ ...c, heading: e.target.value }))
              }
              placeholder="Ready to Connect Your Customers, Sales, and Operations?"
            />
          </Field>
          <Field label="Description">
            <TextArea
              value={finalCta.description}
              onChange={(e) =>
                setFinalCta((c) => ({ ...c, description: e.target.value }))
              }
              placeholder="Supporting text"
            />
          </Field>
          <CTAGroup
            label="Primary Button"
            text={ctaPrimary.text}
            url={ctaPrimary.url}
            newTab={ctaPrimary.newTab}
            onTextChange={(v) => setCtaPrimary((b) => ({ ...b, text: v }))}
            onUrlChange={(v) => setCtaPrimary((b) => ({ ...b, url: v }))}
            onNewTabChange={(v) => setCtaPrimary((b) => ({ ...b, newTab: v }))}
          />
          <CTAGroup
            label="Secondary Button"
            text={ctaSecondary.text}
            url={ctaSecondary.url}
            newTab={ctaSecondary.newTab}
            onTextChange={(v) => setCtaSecondary((b) => ({ ...b, text: v }))}
            onUrlChange={(v) => setCtaSecondary((b) => ({ ...b, url: v }))}
            onNewTabChange={(v) =>
              setCtaSecondary((b) => ({ ...b, newTab: v }))
            }
          />
        </CollapsibleSection>
      )}

      {/* ===================== FAQ ===================== */}
      {template.faq && (
        <CollapsibleSection title="FAQ Section">
          <p className="text-xs text-muted-foreground">
            Questions buyers ask about THIS product (4–6). Answer in 40–80
            words, first sentence answers the question. Leave empty to hide
            the FAQ on the website.
          </p>
          <Field label="Heading">
            <TextInput
              value={faqHeading}
              onChange={(e) => setFaqHeading(e.target.value)}
              placeholder={`e.g. ${title || "Product"} — Frequently Asked Questions`}
            />
          </Field>
          <SectionDivider title="Questions" />
          {faqs.map((f, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Question {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={faqs.length}
                    onMove={(from, to) => setFaqs(moveItem(faqs, from, to))}
                  />
                  {faqs.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setFaqs((ff) => ff.filter((_, idx) => idx !== i))
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <Field label="Question">
                <TextInput
                  value={f.q}
                  onChange={(e) =>
                    setFaqs((ff) =>
                      ff.map((x, idx) =>
                        idx === i ? { ...x, q: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="e.g. Does Power BI connect live to SAP Business One?"
                />
              </Field>
              <Field label="Answer">
                <TextArea
                  value={f.a}
                  onChange={(e) =>
                    setFaqs((ff) =>
                      ff.map((x, idx) =>
                        idx === i ? { ...x, a: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="40–80 words"
                />
              </Field>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setFaqs((ff) => [...ff, { q: "", a: "" }])}
            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
            style={{
              background: "hsl(var(--accent-muted))",
              color: "hsl(var(--accent))",
            }}
          >
            <Plus className="w-3.5 h-3.5" /> Add Question
          </button>
        </CollapsibleSection>
      )}

      {/* ===================== SEO ===================== */}
      {template.seo && (
        <SEOSection seoData={seoData} onChange={handleSeoChange} />
      )}

      <FormActions
        onCancel={() => navigate("/products")}
        onSaveDraft={() => handleSave("draft")}
        onPublish={() => handleSave("published")}
        loading={loading}
      />
    </div>
  );
}
